import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LoginIcon from '@mui/icons-material/Login';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import {
  Button,
  Grid,
  Theme,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { useIsMobile } from '@common/utils/hooks';

export type AccountButtonsProps = {
  name: string;
  onClickLogin: () => void;
  onClickLogout: () => void;
  onClickMemberProfileMenuItem: () => void;
  onClickSignUp: () => void;
  userIsLoggedIn: boolean;
};

/**
 * Header account controls switching between logged-in and logged-out states.
 *
 * When `userIsLoggedIn` is true, renders the member profile button with a
 * dropdown menu for the member menu and logout actions. Otherwise, renders
 * the sign-up and login entry points side by side.
 *
 * @param props - The component props.
 * @param props.name - Display name of the logged-in member; falls back to
 *   「明治会員」 when empty.
 * @param props.onClickLogin - Handler fired when the login button is clicked.
 * @param props.onClickLogout - Handler fired when the logout menu item is
 *   selected.
 * @param props.onClickMemberProfileMenuItem - Handler fired when the member
 *   menu item is selected.
 * @param props.onClickSignUp - Handler fired when the sign-up button is
 *   clicked.
 * @param props.userIsLoggedIn - Whether the current user is authenticated;
 *   toggles between the profile menu and the sign-up/login pair.
 * @returns The rendered account-controls section of the header.
 */
export function AccountButtons({
  name,
  onClickLogin,
  onClickLogout,
  onClickMemberProfileMenuItem,
  onClickSignUp,
  userIsLoggedIn,
}: AccountButtonsProps) {
  const isMobile = useIsMobile();

  if (userIsLoggedIn) {
    return (
      <AccountProfile
        onClickLogout={onClickLogout}
        onClickMemberProfileMenuItem={onClickMemberProfileMenuItem}
        name={name}
      />
    );
  }

  return (
    <Grid
      container
      data-test-id="account-buttons-selector"
      alignItems="center"
      justifyContent="space-between"
      gap={isMobile ? '8px' : '16px'}
    >
      <Grid item>
        <AccountSignUp onClickSignUp={onClickSignUp} />
      </Grid>
      <Grid item>
        <AccountLogin onClickLogin={onClickLogin} />
      </Grid>
    </Grid>
  );
}

/**
 * Logged-in member control rendering the profile button and its dropdown menu.
 */
function AccountProfile({
  name,
  onClickLogout,
  onClickMemberProfileMenuItem,
}: Pick<
  AccountButtonsProps,
  'name' | 'onClickLogout' | 'onClickMemberProfileMenuItem'
>) {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Grid
      container
      alignItems="center"
      sx={(theme) => ({
        [theme.breakpoints.up('md')]: {
          minHeight: '90px',
        },
      })}
    >
      <Button
        data-test-id="user-button-selector"
        variant="text"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
        startIcon={
          <SentimentSatisfiedAltIcon
            sx={(theme) => ({
              color: theme.colors.primary,
              height: '20px',
              width: '20px',
            })}
          />
        }
        sx={(theme: Theme) => ({
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          [theme.breakpoints.down('md')]: {
            maxWidth: '170px',
          },
        })}
      >
        <Tooltip title={name || '明治会員'}>
          <Typography variant={isMobile ? 'xxs' : 'md'} noWrap>
            {name || '明治会員'}
          </Typography>
        </Tooltip>
        <Typography variant={isMobile ? 'xxs' : 'md'}>様</Typography>
      </Button>
      <Menu
        data-test-id="menu-modal-selector"
        MenuListProps={{
          sx: {
            minWidth: '160px',
          },
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onClickMemberProfileMenuItem();
          }}
        >
          <Typography data-test-id="member-menu-selector" variant="xs">
            会員メニュー
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onClickLogout();
          }}
        >
          <Typography data-test-id="logout-menu-selector" variant="xs">
            ログアウト
          </Typography>
        </MenuItem>
      </Menu>
    </Grid>
  );
}

/**
 * Logged-out sign-up entry point button.
 */
function AccountSignUp({
  onClickSignUp,
}: Pick<AccountButtonsProps, 'onClickSignUp'>) {
  const isMobile = useIsMobile();

  return (
    <Button
      variant={isMobile ? 'secondary' : 'text'}
      onClick={onClickSignUp}
      sx={(theme) => ({
        color: 'black',
        [theme.breakpoints.down('md')]: {
          lineHeight: '14.4px',
          px: '10px',
          py: '20px',
        },
      })}
    >
      {isMobile ? (
        <Typography variant="xxxs">会員登録</Typography>
      ) : (
        <>
          <ChevronRightIcon
            sx={(theme) => ({
              color: theme.colors.primary,
              height: '24px',
              width: '24px',
            })}
          />
          <Typography variant="md">明治会員IDをお持ちでない方</Typography>
        </>
      )}
    </Button>
  );
}

/**
 * Logged-out login entry point button.
 */
function AccountLogin({
  onClickLogin,
}: Pick<AccountButtonsProps, 'onClickLogin'>) {
  const isMobile = useIsMobile();

  return (
    <Button
      variant="primary"
      onClick={onClickLogin}
      sx={(theme) => ({
        px: '16px',
        py: '21px',
        [theme.breakpoints.down('md')]: {
          lineHeight: '14.4px',
          px: '8px',
          py: '20px',
        },
      })}
    >
      {isMobile ? (
        <>
          <Typography variant="xxxsBold">
            明治会員ID
            <br />
            でログイン
          </Typography>
        </>
      ) : (
        <>
          <LoginIcon
            sx={{
              height: '16px',
              marginRight: '4px',
              width: '16px',
            }}
          />
          <Typography variant="mdBold">明治会員IDでログイン</Typography>
        </>
      )}
    </Button>
  );
}
